import { ChangeDetectorRef, Component, computed, Inject, OnDestroy, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import {
  Event as RouterEvent,
  GuardsCheckEnd,
  GuardsCheckStart,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Params,
  ResolveEnd,
  ResolveStart,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterModule,
  RoutesRecognized,
} from '@angular/router';
import {
  IonApp, IonMenu, IonContent, IonList, IonListHeader, IonMenuToggle, IonItem,
  IonIcon, IonLabel, IonRouterOutlet, IonRouterLink, IonHeader, IonToolbar, IonButton, ModalController,
  IonAccordionGroup,
  IonSearchbar,
  IonAccordion,
  IonBadge
} from '@ionic/angular/standalone';
import { CommonModule } from '@angular/common';

import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';

import { Geolocation } from '@capacitor/geolocation';
import { Subject, Subscription, combineLatest } from 'rxjs';
import { map, distinctUntilChanged, filter, take } from 'rxjs/operators';

import {
  AuthenticationService, DataService, Global, IGlobal, SecurityService, SignalRCoreService, ThemeService, UtilityService, PushService, TagCacheService,
  InAppGuideService, GETTING_STARTED_GUIDE, InAppGuideComponent, buildGateSummaryTarget, RouteHistoryService
} from 'shared-lib';
import { FavoriteListComponent } from 'shared-lib/components/favorite-list/favorite-list.component';
import { initializeLinq, IEnumerable, ArrayEnumerable } from 'linq-to-typescript';
import { FormsModule } from '@angular/forms';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { DialogModule } from 'primeng/dialog';
import { ITag } from 'shared-lib/_models/tag.model';

declare global {
  interface Array<T> extends IEnumerable<T> {
    concat(items: IEnumerable<T>): IEnumerable<T>;
    concat(...items: Array<ReadonlyArray<T>>): ArrayEnumerable<T>;
    concat(...items: Array<T | ReadonlyArray<T>>): ArrayEnumerable<T>;
  }
  interface Uint8Array extends IEnumerable<number> { }
  interface Uint8ClampedArray extends IEnumerable<number> { }
  interface Uint16Array extends IEnumerable<number> { }
  interface Uint32Array extends IEnumerable<number> { }
  interface Int8Array extends IEnumerable<number> { }
  interface Int16Array extends IEnumerable<number> { }
  interface Int32Array extends IEnumerable<number> { }
  interface Float32Array extends IEnumerable<number> { }
  interface Float64Array extends IEnumerable<number> { }
  interface Map<K, V> extends IEnumerable<[K, V]> { }
  interface Set<T> extends IEnumerable<T> { }
  interface String extends IEnumerable<string> { }
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    RouterModule, RouterLink, RouterLinkActive,
    IonApp, IonMenu, IonContent, IonList, IonListHeader, IonMenuToggle, IonItem,
    IonIcon, IonLabel, IonRouterLink, IonRouterOutlet, IonHeader, IonToolbar, IonButton,
    CommonModule, IonAccordionGroup, IonSearchbar, IonAccordion, FormsModule, DialogModule, IonBadge,
    InAppGuideComponent
],
  standalone: true,
})
export class AppComponent implements OnInit, OnDestroy {
   private siteAlarmCountMap = new Map<number, Signal<number>>();
  private siteAlarmColorMap = new Map<number, Signal<string>>();
  private siteAssetCountMap = new Map<number, number>();

  organizationActiveAlarmTags: Signal<ITag[]> = signal([]);
  organizationAlarmCount: Signal<number> = signal(0);
  organizationAlarmColor: Signal<string> = signal('medium');
  organizationAssetCount: WritableSignal<number> = signal(0);

  public userOrganizationId: number | undefined;
  public organizationName: string = '';
  private pushInitStarted = false;
  public appPages = [
    // { title: 'Home', url: '/', icon: 'car' },

    { title: 'Settings', url: '/settings', icon: 'settings' },
    { title: 'All Assets', url: '/organization-summary/assets-view', icon: 'list' },
  ];

  public global: IGlobal = Global;
  public hubConnectionState = '';
  public countOfSignalRObservations = 0;
  public theme: string = Global.Theme;

  public userIsBeingAuthenticated = false;
  public userIsBeingProcessed = false;
  public status = 'Logging in...';

  public latitude$ = new Subject<number>();
  public longitude$ = new Subject<number>();

  private themeChangeSub!: Subscription;
  private authStateSub!: Subscription;
  private hubStateSub!: Subscription;
  private signalRCountSub!: Subscription;
  private menuRouteSub!: Subscription;
  private userIsBeingProcessed$!: Subscription;

  // Debug subs
  private dbgSubs: Subscription[] = [];
  public signalRTestVisible: boolean = false;
  // State
  isOrgExpanded = true;
  isFavoritesExpanded = false;
  isHistoryExpanded = true;
  siteFilter = '';
  expandedSiteIds: Array<number | string> = [];
  private oauthHandling = false;
  organizationHasGSEAssets: boolean = false;
  organizationHasGSAssets: boolean = false;
  organizationHasCameraFeeds: boolean = false;
  private readonly pushDebugKey = 'iopsPushDebugTrail';
  private readonly videoDiagTrailKey = 'iopsVideoDiagnosticsTrail';
  private readonly videoDiagMaxEntries = 1000;
  private videoDiagEnabled = false;
  private videoDiagUnlisten: Array<() => void> = [];
  private lastVideoInteraction: any = null;
  private originalRouterNavigate?: (commands: any[], extras?: any) => Promise<boolean>;
  private originalRouterNavigateByUrl?: (url: any, extras?: any) => Promise<boolean>;
  private lastSystemLiveNavigation:
    | { url: string; tsMs: number; navId?: number }
    | null = null;
  private tokenManagerErrorHandler?: (err: any) => void;

  get isAdminUser(): boolean {
    return !!(
      this.global?.User?.isAdmin ||
      this.global?.User?.currentUser?.IsSystemAdministrator ||
      (this.global?.User?.currentUser?.Organization?.Id === 17)
    );
  }

  get organizationGateRoute(): string {
    const width = typeof window !== 'undefined' ? window.innerWidth : 0;
    const target = buildGateSummaryTarget(width).primary.tab;
    return `/organization-summary/${target}`;
  }

  get isOrganizationGateActive(): boolean {
    const url = this.router.url || '';
    return url.includes('/organization-summary/gate-overview') || url.includes('/organization-summary/gates-view');
  }

  // Filtering (unchanged)
  filteredSites() {
    const sites = this.global?.User?.currentUser?.SelectedOrganizationSites ?? [];
    const q = (this.siteFilter ?? '').trim().toLowerCase();

    const filtered = q
      ? sites.filter((s: any) => (s?.Name ?? '').toLowerCase().includes(q))
      : sites;

    // Return a NEW array, sorted A→Z by Name
    return [...filtered].sort((a: any, b: any) =>
      (a?.Name ?? '').localeCompare(b?.Name ?? '', undefined, {
        sensitivity: 'base',
        numeric: true,
      })
    );
  }

  trackSite = (_: number, s: any) => String(s?.Id ?? _);
  // Controls
  // Expand only visible (filtered) sites — as strings
  expandAll() {
    this.expandedSiteIds = this.filteredSites().map((s: any) => String(s.Id));
  }

  collapseAll() {
    this.expandedSiteIds = [];
  }

  onAccordionChange(ev: CustomEvent) {
    const raw = ev.detail.value ?? [];
    this.expandedSiteIds = Array.isArray(raw) ? raw.map(v => String(v)) : [];
  }

  onOrgAccordionChange(ev: CustomEvent) {
    const val = ev.detail.value;
    this.isOrgExpanded = Array.isArray(val) ? val.includes('organization') : val === 'organization';
  }

  onFavoritesAccordionChange(ev: CustomEvent) {
    const val = ev.detail.value;
    this.isFavoritesExpanded = Array.isArray(val) ? val.includes('favorites') : val === 'favorites';
  }

  onHistoryAccordionChange(ev: CustomEvent) {
    const val = ev.detail.value;
    this.isHistoryExpanded = Array.isArray(val) ? val.includes('history') : val === 'history';
  }

  clearHistory(): void {
    this.routeHistory.clear();
  }

  openGettingStartedGuide(): void {
    this.inAppGuideService.openGuide(GETTING_STARTED_GUIDE);
  }

  constructor(
    public dataService: DataService,
    private router: Router,
    public signalR: SignalRCoreService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private authState: OktaAuthStateService,
    public securityService: SecurityService,
    public utilityService: UtilityService,
    private modalCtrl: ModalController,
    private authenticationService: AuthenticationService,
    public themeService: ThemeService,
    public pushService: PushService,
    public tagCacheService: TagCacheService,
    public inAppGuideService: InAppGuideService,
    public routeHistory: RouteHistoryService,
    private cdr: ChangeDetectorRef,
  ) {

    initializeLinq();
    //-- the next line is to figure out if the user has their overall mobile device set to a dark theme.  If so, then we're forcing the dark theme on this application. --Kirk T. Sherer, February 12, 2021.
    Global.User.prefersDarkTheme = window.matchMedia("(prefers-color-scheme: dark)").matches;
    Global.isMobile = true;
    // Theme changes
    this.themeChangeSub = this.themeService.theme$.subscribe(() => {
      this.theme = this.themeService.getCurrentTheme();
    });
    console.log(Global);

    this.securityService.userIsBeingProcessed$.subscribe((processing: boolean) => {
        this.userIsBeingProcessed = processing;
    });

    this.securityService.userIsBeingAuthenticated$.subscribe((authenticating: boolean) => {
       this.userIsBeingAuthenticated = authenticating;
    });

    //-- This 'prefersDarkTheme' check is required if the user has set their overall mobile device to a dark mode theme.  If that happens, we MUST force the iOPS
    //-- application to be loaded in a dark mode them and we also are removing the ability to change the iOPS Dark/Light mode theme in the User Settings
    //-- because the application can't override the mobile device's dark mode setting.  They have a message on the Dark/Light mode setting field
    //-- that tells them they must change their overall mobile device back to a normal theme and only then can you change the iOPS theme from dark to light.
    //------- Kirk T. Sherer, August 12, 2022.

    if (Global.User.prefersDarkTheme) {
      const body = document.getElementsByTagName("body")[0];
      body.style.background = "black";
      this.dataService.setTheme("dark"); //--set the theme to dark mode since the user has set their overall dark mode setting on their phone. --Kirk T. Sherer, February 11, 2021.
    } else {
      this.dataService.setTheme(Global.DefaultThemeForApplication);
    }

    // SignalR diagnostics
    this.hubStateSub = this.signalR.hubConnectionState$.subscribe(s => this.hubConnectionState = s);
    this.signalRCountSub = this.signalR.countOfSignalRObservations$.subscribe(c => this.countOfSignalRObservations = c);

    // Dynamic menu route (if used)
    this.menuRouteSub = this.utilityService.menuOverviewRoute$.subscribe(route => {
      const row = this.appPages.find(p => p.title === 'Overview');
      if (row) row.url = route;
    });

    // Okta emits invalid_grant here when a persisted refresh token can no longer renew.
    // Treat it as a local auth reset and send the user through a fresh login.
    this.tokenManagerErrorHandler = (err: any) => {
      console.warn('[Okta] tokenManager error', this.toDebugValue(err));
      this.pushDebug('okta.tokenManager.error', { err: this.toDebugValue(err) });
      if (this.authenticationService.isInvalidGrantError(err)) {
        void this.authenticationService
          .recoverFromInvalidGrant('app-component.tokenManager.error', err)
          .catch((recoveryErr) => {
            console.error('[Okta] invalid_grant recovery failed', this.toDebugValue(recoveryErr));
          });
      }
    };
    this.oktaAuth.tokenManager?.on?.('error', this.tokenManagerErrorHandler);

    CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
      console.log('[appUrlOpen] url:', url);
      this.pushDebug('appUrlOpen.received', {
        url,
        oauthHandling: this.oauthHandling,
        routerUrl: this.router.url,
      });

      const isOktaCallback =
        url?.startsWith('com.oshkosh.iops:/login/callback') ||
        url?.startsWith('com.oshkosh.iops://login/callback') ||
        url?.startsWith('com.oshkosh.iops:/callback') ||
        url?.startsWith('com.oshkosh.iops://callback'); // legacy path; keep to avoid breakage
      this.pushDebug('appUrlOpen.classified', { isOktaCallback });

      if (!isOktaCallback || this.oauthHandling) {
          const urlObj = new URL(url); // Parse the URL
          console.log('[appUrlOpen] urlObj:', urlObj);
          //const path = urlObj.pathname; // Get the path from the URL
          const fullPath = urlObj.pathname + urlObj.search; // Get the full path including query parameters

          console.log('[appUrlOpen] fullPath:', fullPath);
          this.pushDebug('appUrlOpen.navigate.attempt', {
            fullPath,
            routerUrl: this.router.url,
          });
          await this.router.navigateByUrl(fullPath)
            .then((ok) => this.pushDebug('appUrlOpen.navigate.result', {
              ok,
              fullPath,
              routerUrl: this.router.url,
            }))
            .catch((err) => {
              this.pushDebug('appUrlOpen.navigate.error', { err: this.toDebugValue(err) });
            });
          return;
      }

      this.oauthHandling = true;
      this.pushDebug('appUrlOpen.okta.start');
      try {
        // 1) Parse & get tokens
        const { tokens } = await this.oktaAuth.token.parseFromUrl({ url });
        console.log('[Okta] parseFromUrl success');

        // 2) ✅ Explicitly store them
        await this.oktaAuth.tokenManager.setTokens(tokens);

        // 2b) Close any in-app auth session that is still showing (SFSafariViewController/Custom Tabs)
        await Browser.close().catch(() => {});

        // (Optional) sanity logs
        const at = await this.oktaAuth.tokenManager.get('accessToken');
        const it = await this.oktaAuth.tokenManager.get('idToken');
        console.log('stored accessToken?', !!at, 'idToken?', !!it);

        // 3) ✅ Tell the auth state manager to recompute
        await this.oktaAuth.authStateManager.updateAuthState();

        // 4) Check auth and restore the original route
        console.log('[Okta] isAuthenticated:', await this.oktaAuth.isAuthenticated());

        const original = this.oktaAuth.getOriginalUri();
        const target = original || '/organization-summary';
        this.pushDebug('appUrlOpen.okta.redirect-target', { original, target });
        sessionStorage.setItem('postAuthRedirect', target);
        this.oktaAuth.removeOriginalUri();

        // Always funnel through the dedicated authenticating screen
        await this.router.navigateByUrl('/authenticating', { replaceUrl: true });
      } catch (e) {
        console.error('[Okta] parseFromUrl error:', e);
        this.pushDebug('appUrlOpen.okta.parse.error', { err: this.toDebugValue(e) });
        if (this.authenticationService.isInvalidGrantError(e)) {
          await this.authenticationService.recoverFromInvalidGrant('app-component.appUrlOpen.parseFromUrl', e);
        }
      } finally {
        this.oauthHandling = false;
        this.pushDebug('appUrlOpen.okta.done', { routerUrl: this.router.url });
      }
    });
  }

  async ngOnInit(): Promise<void> {

    await this.authenticationService.resetAuthStateAfterNativeVersionChange('app-component.ngOnInit');
    this.setUpEnvironment();
    this.setupVideoDiagnostics();

    (async () => {
      try {
        console.log('[http diag] fetch test starting');
        const r = await fetch(
          'https://osk.okta.com/oauth2/default/.well-known/openid-configuration',
          { headers: { Accept: 'application/json' } }
        );
        console.log('[http diag] fetch test status:', r.status);
      } catch (e) {
        console.error('[http diag] fetch test error:', e);
      }
    })();
    this.theme = this.themeService.getCurrentTheme();

    // Track authentication state for downstream logic (push init, etc.)
    const ready$ = combineLatest([this.authState.authState$, this.dataService.cacheReady$]).pipe(
      map(([s, cacheReady]) => !!s.isAuthenticated && !!cacheReady),
      distinctUntilChanged()
    );

    // Keep asset counts and scope flags in sync with asset type group changes
    this.dbgSubs.push(
      this.utilityService.assetTypeGroupChange$.subscribe(() => {
        this.recomputeAssetCounts(this.dataService.cache);
        this.updateOrgAssetFlags();
        this.tagCacheService.refreshForScopeChange();
        this.cdr.markForCheck();
      })
    );

    // Keep All Assets counts in sync with the data cache
    this.dbgSubs.push(
      this.dataService.fullCache$
        .pipe(filter((cache) => !!cache))
        .subscribe((cache) => this.recomputeAssetCounts(cache))
    );

    // (Optional) logs
    this.dbgSubs.push(
      ready$.subscribe(async (ready) => {
        this.pushDebug('ready.state', { ready, pushInitStarted: this.pushInitStarted, routerUrl: this.router.url });
        if (ready && !this.pushInitStarted) {
          this.pushInitStarted = true;
          try {
            this.pushDebug('push.init.start');
            await this.pushService.init();
            await this.pushService.initLocalListeners();
            console.log('[Push] Initialized');
            this.pushDebug('push.init.done');
          } catch (e) {
            console.error('[Push] init failed:', e);
            this.pushDebug('push.init.error', { err: this.toDebugValue(e) });
            this.pushInitStarted = false; // optional: allow retry if you want
          }
        }
      })
    );

    this.dbgSubs.push(
      ready$
        .pipe(
          filter(v => v === true),
          take(1) // fire once
        )
        .subscribe(() => {
          let adminOrganizationIdSelected: boolean = Global.User.currentUser?.AdministratorOrganizationId ? true : false;
          console.log('adminOrganizationIdSelected: ' + adminOrganizationIdSelected);
          this.userOrganizationId =
            Global.User.currentUser?.AdministratorOrganizationId ?? Global.User.currentUser?.Organization?.Id;
          console.log('this.userOrganizationId: ' + this.userOrganizationId);

          console.log(this.dataService.cache.organizations);
          this.organizationName = adminOrganizationIdSelected
            ? (
                this.dataService.cache.organizations
                  .find((o: any) => o.Id === this.userOrganizationId)?.Name
                ?? Global.User.currentUser?.Organization?.Name
                ?? ''
              )
            : (Global.User.currentUser?.Organization?.Name ?? '');

            console.log(this.appPages)
            this.updateOrgAssetFlags();
            this.organizationHasCameraFeeds = this.dataService.determineIfOrganizationHasCameraFeeds();
        })
    );

    this.authStateSub = this.authState.authState$.subscribe(async (state) => {
      if (state.isAuthenticated) {
        await this.processLoggedInUser().catch(async (err) => {
          console.error('[Okta] processLoggedInUser from authState failed', this.toDebugValue(err));
          if (this.authenticationService.isInvalidGrantError(err)) {
            await this.authenticationService.recoverFromInvalidGrant('app-component.authState.processLoggedInUser', err);
          }
        });
      }
    });

     this.organizationActiveAlarmTags = this.tagCacheService.getAlarmTagsForOrganization();

    // Derived signals
    this.organizationAlarmCount = computed(
      () => this.organizationActiveAlarmTags().length ?? 0
    );

    this.organizationAlarmColor = computed(() => {
      const tags = this.organizationActiveAlarmTags();
      if (!tags?.length) return 'medium';       // grey if none
      const hasCritical = tags.some(t => t.Severity === 'Critical');
      const hasAlarm = tags.some(t => t.Severity === 'Alarm');
      if (hasCritical) return 'danger';         // red if any Critical
      if (hasAlarm) return 'warning';           // orange if only Alarm(s)
      return 'medium';
    });

  }

  ngOnDestroy(): void {
    this.themeChangeSub?.unsubscribe();
    this.authStateSub?.unsubscribe();
    this.hubStateSub?.unsubscribe();
    this.signalRCountSub?.unsubscribe();
    this.menuRouteSub?.unsubscribe();
    if (this.tokenManagerErrorHandler) {
      this.oktaAuth.tokenManager?.off?.('error', this.tokenManagerErrorHandler);
      this.tokenManagerErrorHandler = undefined;
    }
    this.dbgSubs.forEach(s => s.unsubscribe());
    this.videoDiagUnlisten.forEach(unlisten => unlisten());
    this.videoDiagUnlisten = [];
    this.unpatchRouterNavigationDiagnostics();
  }

  private setupVideoDiagnostics(): void {
    this.videoDiagEnabled = true; // temporary: always on while diagnosing mobile video routing/tap behavior
    if (!this.videoDiagEnabled || typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    this.videoDiagLog('diag.enabled', {
      route: this.router.url,
      href: window.location.href,
      userAgent: navigator.userAgent,
    });

    this.patchRouterNavigationDiagnostics();

    const interactionHandler = (event: Event) => {
      const route = this.router.url || '';
      if (!this.isVideoRoute(route)) {
        return;
      }

      const payload = this.buildInteractionPayload(event);
      this.lastVideoInteraction = payload;
      this.videoDiagLog('ui.interaction', payload);
    };

    const interactionEvents = ['touchstart', 'touchend', 'pointerdown', 'pointerup', 'mousedown', 'mouseup', 'click'];
    for (const eventName of interactionEvents) {
      document.addEventListener(eventName, interactionHandler, true);
      this.videoDiagUnlisten.push(() => document.removeEventListener(eventName, interactionHandler, true));
    }

    const routerDiagSub = this.router.events.subscribe((event: RouterEvent) => this.recordVideoRouterEvent(event));
    this.dbgSubs.push(routerDiagSub);

    // Convenience hooks for remote devtools on physical device
    (window as any).iopsGetVideoDiagTrail = () => {
      try {
        return JSON.parse(localStorage.getItem(this.videoDiagTrailKey) || '[]');
      } catch {
        return [];
      }
    };
    (window as any).iopsClearVideoDiagTrail = () => {
      localStorage.removeItem(this.videoDiagTrailKey);
    };
  }

  private patchRouterNavigationDiagnostics(): void {
    const routerAny = this.router as any;
    if (this.originalRouterNavigate || this.originalRouterNavigateByUrl) {
      return;
    }

    this.originalRouterNavigate = routerAny.navigate.bind(this.router);
    this.originalRouterNavigateByUrl = routerAny.navigateByUrl.bind(this.router);

    routerAny.navigate = (commands: any[], extras?: any) => {
      const target = this.describeNavigateCommands(commands, extras);
      if (this.isVideoRelatedNavigation(this.router.url, target.pathGuess)) {
        this.videoDiagLog('router.navigate.call', {
          fromUrl: this.router.url,
          commands: this.toDebugValue(commands),
          extras: this.sanitizeNavigationExtras(extras),
          target,
          stack: this.getShortStack(),
        });
      }
      return this.originalRouterNavigate!(commands, extras);
    };

    routerAny.navigateByUrl = (url: any, extras?: any) => {
      const urlText = this.describeUrlLike(url);
      if (this.isVideoRelatedNavigation(this.router.url, urlText)) {
        this.videoDiagLog('router.navigateByUrl.call', {
          fromUrl: this.router.url,
          url: urlText,
          extras: this.sanitizeNavigationExtras(extras),
          stack: this.getShortStack(),
        });
      }
      return this.originalRouterNavigateByUrl!(url, extras);
    };
  }

  private unpatchRouterNavigationDiagnostics(): void {
    const routerAny = this.router as any;
    if (this.originalRouterNavigate) {
      routerAny.navigate = this.originalRouterNavigate;
      this.originalRouterNavigate = undefined;
    }
    if (this.originalRouterNavigateByUrl) {
      routerAny.navigateByUrl = this.originalRouterNavigateByUrl;
      this.originalRouterNavigateByUrl = undefined;
    }
  }

  private recordVideoRouterEvent(event: RouterEvent): void {
    const eventUrl = this.extractRouterEventUrl(event);
    const currentUrl = this.router.url || '';
    const shouldLog = this.isVideoRoute(eventUrl) || this.isVideoRoute(currentUrl);
    if (!shouldLog) {
      return;
    }

    const isTrackedEvent =
      event instanceof NavigationStart ||
      event instanceof RoutesRecognized ||
      event instanceof GuardsCheckStart ||
      event instanceof GuardsCheckEnd ||
      event instanceof ResolveStart ||
      event instanceof ResolveEnd ||
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError;

    if (!isTrackedEvent) {
      return;
    }

    const payload: any = {
      eventType: event.constructor.name,
      navId: (event as any).id,
      eventUrl,
      currentUrl,
      trigger: (event as any).navigationTrigger,
      restoredState: (event as any).restoredState,
      reason: (event as any).reason,
      error: this.toDebugValue((event as any).error),
      lastVideoInteraction: this.lastVideoInteraction,
    };

    if (event instanceof NavigationEnd) {
      payload.urlAfterRedirects = event.urlAfterRedirects;
      this.trackRapidSystemToCameraTransition(event.urlAfterRedirects, (event as any).id);
    }
    if (event instanceof RoutesRecognized) {
      payload.urlAfterRedirects = event.urlAfterRedirects;
    }

    this.videoDiagLog('router.event', payload);
  }

  private extractRouterEventUrl(event: RouterEvent): string {
    const anyEvent: any = event as any;
    if (typeof anyEvent.urlAfterRedirects === 'string' && anyEvent.urlAfterRedirects.length > 0) {
      return anyEvent.urlAfterRedirects;
    }
    if (typeof anyEvent.url === 'string' && anyEvent.url.length > 0) {
      return anyEvent.url;
    }
    return '';
  }

  private isVideoRoute(url: string | undefined | null): boolean {
    if (!url) {
      return false;
    }
    return url.includes('/videos');
  }

  private isSystemLiveRoute(url: string | undefined | null): boolean {
    if (!url) return false;
    return /\/videos\/system\/[^/]+\/live(?:$|[/?#])/.test(url);
  }

  private isCameraRoute(url: string | undefined | null): boolean {
    if (!url) return false;
    if (/\/videos\/camera\/[^/]+(?:$|[/?#])/.test(url)) return true;
    if (/\/videos\/system\/[^/]+\/live\/camera\/[^/]+(?:$|[/?#])/.test(url)) return true;
    return false;
  }

  private trackRapidSystemToCameraTransition(urlAfterRedirects: string, navId?: number): void {
    const now = Date.now();

    if (this.isSystemLiveRoute(urlAfterRedirects)) {
      this.lastSystemLiveNavigation = {
        url: urlAfterRedirects,
        tsMs: now,
        navId,
      };
      this.videoDiagLog('system-live.entered', {
        url: urlAfterRedirects,
        navId,
      });
      return;
    }

    if (!this.lastSystemLiveNavigation) {
      return;
    }

    if (!this.isCameraRoute(urlAfterRedirects)) {
      return;
    }

    const elapsedMs = now - this.lastSystemLiveNavigation.tsMs;
    if (elapsedMs > 5000) {
      return;
    }

    this.videoDiagLog('anomaly.systemLiveToCameraRapid', {
      fromSystemLiveUrl: this.lastSystemLiveNavigation.url,
      toCameraUrl: urlAfterRedirects,
      elapsedMs,
      fromNavId: this.lastSystemLiveNavigation.navId,
      toNavId: navId,
      lastVideoInteraction: this.lastVideoInteraction,
    });
  }

  private isVideoRelatedNavigation(currentUrl: string | undefined | null, targetUrl: string | undefined | null): boolean {
    return this.isVideoRoute(currentUrl) || this.isVideoRoute(targetUrl);
  }

  private describeNavigateCommands(commands: any[], extras?: any): { joined: string; pathGuess: string; relativeTo?: string } {
    const joined = Array.isArray(commands)
      ? commands
          .map((command) => {
            if (typeof command === 'string' || typeof command === 'number' || typeof command === 'boolean') {
              return String(command);
            }
            try {
              return JSON.stringify(command);
            } catch {
              return String(command);
            }
          })
          .join('/')
      : String(commands ?? '');

    const from = this.router.url || '';
    const normalizedJoined = joined.startsWith('/') ? joined : `/${joined}`;
    const pathGuess = joined.startsWith('/') ? joined : `${from.replace(/\/+$/, '')}${normalizedJoined}`;

    return {
      joined,
      pathGuess,
      relativeTo: this.describeActivatedRoute(extras?.relativeTo),
    };
  }

  private describeUrlLike(url: any): string {
    if (typeof url === 'string') {
      return url;
    }
    if (url && typeof url.toString === 'function') {
      try {
        return url.toString();
      } catch {
        return '[url.toString failed]';
      }
    }
    return String(url);
  }

  private sanitizeNavigationExtras(extras: any): any {
    if (!extras) {
      return undefined;
    }
    return {
      replaceUrl: extras.replaceUrl,
      skipLocationChange: extras.skipLocationChange,
      queryParams: this.toDebugValue(extras.queryParams),
      queryParamsHandling: extras.queryParamsHandling,
      fragment: extras.fragment,
      preserveFragment: extras.preserveFragment,
      state: this.toDebugValue(extras.state),
      relativeTo: this.describeActivatedRoute(extras.relativeTo),
    };
  }

  private describeActivatedRoute(route: any): string | undefined {
    if (!route) {
      return undefined;
    }

    const path = route?.snapshot?.routeConfig?.path;
    const urlSegments = route?.snapshot?.url?.map((segment: any) => segment.path).join('/');
    const pieces = [path, urlSegments].filter((piece) => typeof piece === 'string' && piece.length > 0);
    return pieces.length ? pieces.join(' | ') : undefined;
  }

  private getShortStack(maxFrames: number = 8): string[] {
    const stack = new Error().stack || '';
    const lines = stack.split('\n').map((line) => line.trim()).filter((line) => line.length > 0);
    return lines.slice(2, 2 + maxFrames);
  }

  private buildInteractionPayload(event: Event): any {
    const mouseLike = event as MouseEvent;
    const target = event.target as Element | null;

    return {
      type: event.type,
      ts: Date.now(),
      route: this.router.url,
      detail: typeof mouseLike.detail === 'number' ? mouseLike.detail : undefined,
      pointerType: (event as any).pointerType,
      isTrusted: event.isTrusted,
      target: this.describeElement(target),
      activeElement: this.describeElement(document.activeElement),
      x: typeof mouseLike.clientX === 'number' ? mouseLike.clientX : undefined,
      y: typeof mouseLike.clientY === 'number' ? mouseLike.clientY : undefined,
    };
  }

  private describeElement(element: Element | null): string {
    if (!element) {
      return 'none';
    }

    const tag = element.tagName?.toLowerCase() ?? 'unknown';
    const id = (element as HTMLElement).id ? `#${(element as HTMLElement).id}` : '';
    const classList = Array.from(element.classList ?? []).slice(0, 3).join('.');
    const cls = classList.length > 0 ? `.${classList}` : '';
    const text = ((element as HTMLElement).innerText || '')
      .trim()
      .replace(/\s+/g, ' ')
      .slice(0, 40);
    const textSuffix = text.length > 0 ? ` "${text}"` : '';
    return `${tag}${id}${cls}${textSuffix}`;
  }

  private videoDiagLog(stage: string, details?: any): void {
    const entry = {
      ts: new Date().toISOString(),
      source: 'mobile.video.diagnostics',
      stage,
      route: this.router.url,
      details: this.toDebugValue(details),
    };

    // console.log('[VideoDiag]', entry);
    try {
      const existing = JSON.parse(localStorage.getItem(this.videoDiagTrailKey) || '[]');
      existing.push(entry);
      if (existing.length > this.videoDiagMaxEntries) {
        existing.splice(0, existing.length - this.videoDiagMaxEntries);
      }
      localStorage.setItem(this.videoDiagTrailKey, JSON.stringify(existing));
    } catch {
      return;
    }
  }

  // --- Post-login bootstrap ---
  private async processLoggedInUser() {
    const userClaims = await this.oktaAuth.getUser();
    const authToken = this.oktaAuth.getAccessToken();

    console.log('[Okta] user = %O', userClaims);
    console.log('[Okta] accessToken present:', !!authToken);

    if (authToken) {
      await this.securityService.processLoggedInUser("mobile: app-component", authToken, userClaims.email);
      this.getLocationWithTimeout(3000).catch(err =>
        console.warn('[Geo] non-blocking getLocation failed:', err)
      );
      // Let cacheReady$ drive final routing; no global splash overlay here.
    }
  }

  private async getLocation() {
    try {
      const pos = await Geolocation.getCurrentPosition();
      this.latitude$.next(pos.coords.latitude);
      this.longitude$.next(pos.coords.longitude);
    } catch (err) {
      console.warn('[Geo] failed:', err);
    }
  }

  private async getLocationWithTimeout(ms = 3000) {
    try {
      const pos = await Promise.race([
        Geolocation.getCurrentPosition({ enableHighAccuracy: false }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('geo-timeout')), ms)
        ),
      ]);
      this.latitude$.next(pos.coords.latitude);
      this.longitude$.next(pos.coords.longitude);
    } catch (err) {
      console.warn('[Geo] failed or timed out:', err);
    }
  }

  private pushDebug(stage: string, details?: any): void {
    const entry = {
      ts: new Date().toISOString(),
      source: 'mobile.app.component',
      stage,
      details: this.toDebugValue(details),
    };
    console.log('[PushDBG]', entry);
    try {
      const existing = JSON.parse(localStorage.getItem(this.pushDebugKey) || '[]');
      existing.push(entry);
      if (existing.length > 250) existing.splice(0, existing.length - 250);
      localStorage.setItem(this.pushDebugKey, JSON.stringify(existing));
    } catch {
      return;
    }
  }

  private toDebugValue(value: any): any {
    if (value == null) return value;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return String(value);
    }
  }

  // --- Utilities ---
  async openEditFavoritesModal() {
    const modal = await this.modalCtrl.create({ component: FavoriteListComponent,       cssClass: 'fullscreen-modal' // 👈 apply the custom class
 });
    await modal.present();
    await modal.onWillDismiss();
  }

  getPathWithoutQuery(path: string): string[] { return [path.split('?')[0]]; }
  getQueryParams(path: string): Params {
    const params = new URLSearchParams(path.split('?')[1] ?? '');
    const result: any = {};
    params.forEach((v, k) => result[k] = v);
    return result;
  }

  trackPage(_i: number, item: any) { return item; }

  get isDark(): boolean {
    return (this.theme || '').toLowerCase().trim() === 'dark';
  }

  private setUpEnvironment() {

    var currentEnvironment = "prod";

    if ((window.location.origin.indexOf("localhost") > -1 || window.location.origin.indexOf("test") > -1) && window.location.origin.indexOf("beta") <= -1) {
      currentEnvironment = "test";
    }
    else {
      if (window.location.origin.indexOf("beta") > -1) {
        currentEnvironment = "beta";
      }
      else {
        currentEnvironment = "prod";
      }
    }

    console.log("currentEnvironment: " + currentEnvironment);
    var environmentId = 1;
    switch (currentEnvironment) {
      case "test":
        environmentId = 3;
        break;
      case "beta":
        environmentId = 2;
        break;
      case "prod":
        environmentId = 1;
        break;
    }

    Global.Application = {
      Environment: {
        Name: currentEnvironment,
        Id: environmentId,
        Version: "2.0",
        Database: "prod"
      },
      Type: "mobile"
    };

  }

  showSignalRTest() {
     this.signalRTestVisible = this.signalRTestVisible == false;
     console.log("signalRTest invoked... this.signalRTestVisible: " + this.signalRTestVisible);
     this.signalR.LogActivity("User invoked signalR Test route...");
     this.router.navigateByUrl('/signalRTest');

		}

  getSiteAlarmCountSignal(siteId: number): Signal<number> {
    if (!this.siteAlarmCountMap.has(siteId)) {
      const tagsSig = this.tagCacheService.getAlarmTagsForSite(siteId);
      this.siteAlarmCountMap.set(siteId, computed(() => tagsSig().length));
    }
    return this.siteAlarmCountMap.get(siteId)!;
  }

  /** Returns a Signal<string> representing the correct color: medium | warning | danger */
  getSiteAlarmColorSignal(siteId: number): Signal<string> {
    if (!this.siteAlarmColorMap.has(siteId)) {
      const tagsSig = this.tagCacheService.getAlarmTagsForSite(siteId);

      this.siteAlarmColorMap.set(
        siteId,
        computed(() => {
          const tags = tagsSig();
          if (!tags?.length) return 'medium'; // no alarms = grey

          const hasCritical = tags.some(t => t.Severity === 'Critical');
          const hasAlarm = tags.some(t => t.Severity === 'Alarm');

          if (hasCritical) return 'danger';   // red if any Critical
          if (hasAlarm) return 'warning';     // orange if only Alarms
          return 'medium';
        })
      );
    }
    return this.siteAlarmColorMap.get(siteId)!;
  }

  isSiteExpanded(siteId: number): boolean {
    return this.expandedSiteIds?.includes(siteId.toString());
  }

  getSiteAssetCount(siteId: number): number {
    return this.siteAssetCountMap.get(siteId) ?? 0;
  }

  get selectedAssetTypeGroupId(): number | null {
    const id = Global.User?.currentUser?.SelectedAssetTypeGroupId;
    return (id === 1 || id === 2) ? id : null;
  }

  get selectedAssetTypeGroupName(): string {
    const id = this.selectedAssetTypeGroupId;
    if (id === 1) return 'Gate Systems';
    if (id === 2) return 'GSE';
    return '';
  }

  get assetMenuLabel(): string {
    const id = this.selectedAssetTypeGroupId;
    if (id === 1) return 'Gate System Assets';
    if (id === 2) return 'GSE Assets';
    return 'All Assets';
  }

  public updateOrgAssetFlags(): void {
    const activeScope = Global.User?.currentUser?.SelectedAssetTypeGroupId;
    if (activeScope === 1) {
      this.organizationHasGSEAssets = false;
      this.organizationHasGSAssets = true;
    } else if (activeScope === 2) {
      this.organizationHasGSEAssets = true;
      this.organizationHasGSAssets = false;
    } else {
      this.organizationHasGSEAssets = this.dataService.determineIfOrganizationHasGSEAssets();
      this.organizationHasGSAssets = this.dataService.determineIfOrganizationHasGateSystemAssets();
    }
  }

  private isAssetInScope(asset: any, activeScope: number | null | undefined): boolean {
    if (activeScope !== 1 && activeScope !== 2) {
      return true;
    }
    const groupId =
      asset?.AssetType?.AssetTypeGroup?.Id ??
      asset?.AssetType?.AssetTypeGroupId ??
      asset?.AssetTypeGroupId ??
      (asset?.AssetTypeId ? this.dataService.cache?.assetTypesObject?.[asset.AssetTypeId]?.AssetTypeGroupId : undefined);
    return Number(groupId) === activeScope;
  }

  private recomputeAssetCounts(cache: any): void {
    const allAssets: any[] = Array.isArray(cache?.assets) ? cache.assets : [];
    const activeScope = Global.User?.currentUser?.SelectedAssetTypeGroupId;
    const scopedAssets = allAssets.filter(a => this.isAssetInScope(a, activeScope));

    this.organizationAssetCount.set(scopedAssets.length);

    const next = new Map<number, number>();
    for (const asset of scopedAssets) {
      const siteId = Number(asset?.SiteId);
      if (!Number.isFinite(siteId)) continue;
      next.set(siteId, (next.get(siteId) ?? 0) + 1);
    }
    this.siteAssetCountMap = next;
  }
}
